/**
 * Compare Page
 *
 * Side-by-side comparison of multiple mutual funds.
 * Receives ISINs via search params, fetches real data from BFF.
 *
 * Route: /compare?funds[]=ISIN1&funds[]=ISIN2
 */

import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import * as React from "react";
import { useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  GitCompare,
  Plus,
  X,
  AlertCircle,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  useFundCompare,
  getFundCompareErrorMessage,
} from "@/hooks/useFundCompare";
import type { Fund } from "@curie/shared";
import {
  CompareTable,
  CompareTableSkeleton,
  CompareInsights,
  SmallMultiplesChart,
  SmallMultiplesChartSkeleton,
  OverlapDetection,
  ExportComparisonButtonWithLabel,
  ComparisonExportView,
  EMPTY_ISINS,
} from "@/components/compare";
import { GlobalErrorFallback } from "@/components/error";

// ============================================================================
// Route Definition
// ============================================================================

export interface CompareSearch {
  funds?: string[];
}

export const Route = createFileRoute("/compare")({
  component: ComparePage,
  errorComponent: GlobalErrorFallback,
  validateSearch: (search: Record<string, unknown>): CompareSearch => {
    return {
      funds: Array.isArray(search.funds)
        ? search.funds.filter((f): f is string => typeof f === "string")
        : undefined,
    };
  },
});

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Loading skeleton for comparison page
 */
function CompareLoadingSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page header skeleton */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <GitCompare className="h-8 w-8 text-primary" />
          <Skeleton className="h-9 w-48" />
        </div>
        <Skeleton className="mt-2 h-5 w-96" />
      </div>

      {/* Fund badges skeleton */}
      <div className="mb-6 flex items-center gap-2">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-32" />
      </div>

      {/* Compare table skeleton */}
      <CompareTableSkeleton columnCount={3} />

      {/* Small multiples chart skeleton */}
      <div className="mt-6">
        <SmallMultiplesChartSkeleton count={3} />
      </div>

      {/* Insights and overlap detection skeletons */}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="py-6">
            <Skeleton className="h-6 w-32 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-6">
            <Skeleton className="h-6 w-36 mb-4" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Error display component
 */
function ErrorDisplay({
  message,
  onRetry,
  isRetrying,
}: {
  message: string;
  onRetry: () => void;
  isRetrying?: boolean;
}) {
  return (
    <Card className="border-destructive/50 bg-destructive/5">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Failed to load comparison
        </h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">{message}</p>
        <Button
          variant="outline"
          onClick={onRetry}
          disabled={isRetrying}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
          {isRetrying ? "Retrying..." : "Try Again"}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Empty state when no funds selected
 */
function EmptyState() {
  return (
    <Card className="py-12 text-center">
      <CardContent>
        <GitCompare className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">No funds to compare</h3>
        <p className="mt-2 text-muted-foreground">
          Add at least 2 funds from the Explore page to start comparing
        </p>
        <Button className="mt-4" asChild>
          <Link to="/explore">Browse Funds</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Not enough funds state
 */
function NeedMoreFundsState({ currentCount }: { currentCount: number }) {
  return (
    <Card className="py-12 text-center">
      <CardContent>
        <Search className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">Need more funds</h3>
        <p className="mt-2 text-muted-foreground">
          You have {currentCount} fund selected. Add at least 2 funds to compare them.
        </p>
        <Button className="mt-4" asChild>
          <Link to="/explore">Add More Funds</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Funds not found state
 */
function FundsNotFoundState() {
  return (
    <Card className="py-12 text-center">
      <CardContent>
        <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">Funds not found</h3>
        <p className="mt-2 text-muted-foreground">
          The selected funds could not be found. Please try selecting different funds.
        </p>
        <Button className="mt-4" asChild>
          <Link to="/explore">Browse Funds</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Page header component with optional export button
 */
function PageHeader({
  showExport = false,
  exportRef,
  funds = [],
}: {
  showExport?: boolean;
  exportRef?: React.RefObject<HTMLDivElement | null>;
  funds?: Fund[];
}) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitCompare className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Compare Funds</h1>
        </div>
        {showExport && exportRef && funds.length >= 2 && (
          <ExportComparisonButtonWithLabel
            targetRef={exportRef}
            funds={funds}
            label="Export"
          />
        )}
      </div>
      <p className="mt-2 text-muted-foreground">
        Compare mutual funds side-by-side to make informed investment decisions
      </p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function ComparePage() {
  const navigate = useNavigate();
  const { funds: selectedIsins } = Route.useSearch();

  // Ref for the exportable content
  const exportRef = useRef<HTMLDivElement>(null);

  // Use stable empty array constant to prevent re-renders
  const isins = selectedIsins ?? EMPTY_ISINS;

  // Fetch comparison data
  const { data, isLoading, isError, error, refetch, isFetching } =
    useFundCompare(isins);

  const fundsToCompare = data?.comparison ?? [];

  // Handle removing a fund from comparison
  const handleRemoveFund = useCallback(
    (isinToRemove: string) => {
      const newIsins = isins.filter((isin) => isin !== isinToRemove);
      navigate({
        to: "/compare",
        search: { funds: newIsins.length > 0 ? newIsins : undefined },
        replace: true,
      });
    },
    [isins, navigate]
  );

  // Handle adding a fund (navigate to explore)
  const handleAddFund = useCallback(() => {
    navigate({ to: "/explore" });
  }, [navigate]);

  // Render loading state
  if (isLoading) {
    return <CompareLoadingSkeleton />;
  }

  // Render empty state (no ISINs provided)
  if (isins.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader />
        <EmptyState />
      </div>
    );
  }

  // Render need more funds state
  if (isins.length < 2) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader />
        <NeedMoreFundsState currentCount={isins.length} />
      </div>
    );
  }

  // Render error state
  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader />
        <ErrorDisplay
          message={getFundCompareErrorMessage(error)}
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  // Render when no funds found from API
  if (fundsToCompare.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader />
        <FundsNotFoundState />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader
        showExport
        exportRef={exportRef}
        funds={fundsToCompare}
      />

      {/* Selected Funds Badges */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Comparing:</span>
        {fundsToCompare.map((fund) => (
          <Badge key={fund.isin} variant="secondary" className="gap-1 pr-1">
            {fund.name.length > 30 ? `${fund.name.slice(0, 30)}...` : fund.name}
            <button
              onClick={() => handleRemoveFund(fund.isin)}
              className="ml-1 rounded-full p-0.5 hover:bg-muted hover:text-destructive"
              aria-label={`Remove ${fund.name} from comparison`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {fundsToCompare.length < 4 && (
          <Button variant="outline" size="sm" onClick={handleAddFund}>
            <Plus className="mr-1 h-3 w-3" />
            Add Fund
          </Button>
        )}
      </div>

      {/* Main Comparison Table */}
      <CompareTable funds={fundsToCompare} />

      {/* Small Multiples Chart - Rank Trajectory Comparison */}
      <div className="mt-6">
        <SmallMultiplesChart funds={fundsToCompare} rankHistory={data?.rankHistory} />
      </div>

      {/* Insights and Overlap Detection */}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <CompareInsights funds={fundsToCompare} />
        <OverlapDetection funds={fundsToCompare} />
      </div>

      {/* Hidden Export View - rendered off-screen for PDF/image generation */}
      <div
        className="fixed left-[-9999px] top-0 pointer-events-none"
        aria-hidden="true"
      >
        <ComparisonExportView
          ref={exportRef}
          funds={fundsToCompare}
        />
      </div>
    </div>
  );
}
